import {
  EnforcementEnvironment,
  EnforcementPathFact,
  EnforcementReadiness,
  EnforcementReadinessStatus,
} from "@/api/temp/AgentEstateService";
import { tenantEnforcementSnapshot } from "@/libs/tenantEnforcement";
import { type GeneratedAgent, hashString } from "./generate";
import { createScenarioSwitch } from "./scenarioSwitch";

/**
 * Mock TrustLogix enforcement readiness - whether an agent's runtime traffic
 * passes a TrustLogix enforcement path. There is no backend for this yet: the
 * agent record would carry it once the gateway reports observed routes.
 *
 * Readiness is derived, never stored, so it can never drift from the agent
 * population it describes. The rules are deterministic (keyed off the agent
 * id), so the same estate always reads the same way.
 *
 * Default rules ("partial"), in priority order:
 *  - the two named demo scenarios are routed through the gateway;
 *  - Investment Research agents call their MCP service directly (an observed
 *    bypass);
 *  - development-tier agents are not yet routed;
 *  - test-tier agents are all routed (a fully ready group);
 *  - in production, a few agent routes are unknown and a share of Private
 *    Banking & Client Services agents are unrouted.
 *
 * Unknown slots are positional (discovery order). Runtime outage is available
 * only through the explicit demo scenario.
 *
 * `?enforcement=none|partial|ready|outage` switches scenario inside mock scope, the
 * same way `?dashboardState=` drives the board.
 */

export type EnforcementScenario = "none" | "partial" | "ready" | "outage";

const SCENARIOS: readonly EnforcementScenario[] = [
  "none",
  "partial",
  "ready",
  "outage",
];
const scenario = createScenarioSwitch<EnforcementScenario>({
  param: "enforcement",
  storageKey: "__TLX_ENFORCEMENT_SCENARIO__",
  values: SCENARIOS,
  fallback: "partial",
});

/** Specs and demos pin a scenario directly; `undefined` clears the pin. */
export const setEnforcementScenario = scenario.set;

/**
 * The active scenario. A `?enforcement=` param wins and is remembered for the
 * session, so navigating from the board to an agent keeps the same story.
 */
export const getEnforcementScenario = scenario.get;

export function enforcementEnvironmentFor(
  scenario = getEnforcementScenario(),
): EnforcementEnvironment {
  const saved = tenantEnforcementSnapshot();
  return scenario === "outage" && saved.enabled
    ? { ...saved, status: "ATTENTION_REQUIRED", healthStatus: "UNHEALTHY" }
    : saved;
}

export type ReadinessAgent = Pick<
  GeneratedAgent,
  "id" | "name" | "businessUnit" | "environmentType" | "demoScenario"
> & { sequenceIndex?: number };

/** Positions in each window of discovery order that get a non-ready state. */
const SLOT_WINDOW = 19;
const UNKNOWN_SLOTS = [10, 11];

function routed(
  environment: EnforcementEnvironment,
  overrides: Partial<EnforcementReadiness> = {},
): EnforcementReadiness {
  return {
    status: "READY",
    method: environment.method,
    environmentConfigured: true,
    agentRoutedThroughEnforcement: true,
    routingObserved: true,
    healthStatus: environment.healthStatus,
    configurationId: environment.configurationId,
    environmentName: environment.name,
    ...overrides,
  };
}

function unrouted(
  environment: EnforcementEnvironment,
  observed: boolean,
): EnforcementReadiness {
  return {
    status: "SETUP_REQUIRED",
    method: "NONE",
    environmentConfigured: environment.configured,
    agentRoutedThroughEnforcement: false,
    routingObserved: observed,
    reason: !environment.configured
      ? "noEnvironment"
      : observed
        ? "directMcp"
        : "notRouted",
  };
}

/**
 * What every agent's readiness is read against. Resolve it once per store call
 * and pass it to `readinessForAgent`, rather than rebuilding it per agent.
 */
export interface ReadinessContext {
  scenario: EnforcementScenario;
  environment: EnforcementEnvironment;
  now: number;
}

export const readinessContext = (
  scenario = getEnforcementScenario(),
  now = Date.now(),
): ReadinessContext => ({
  scenario,
  environment: enforcementEnvironmentFor(scenario),
  now,
});

/**
 * The gateway-to-MCP-service connection facts for the services an agent uses.
 * Owned by the estate store (it holds the connection profiles), registered here
 * so that `deriveAgentReadiness` stays the one gate every count reads through.
 * Unregistered means no connection facts, so only routing is judged.
 */
export type ReadinessPathResolver = (
  agent: ReadinessAgent,
  now: number,
) => EnforcementPathFact[];

let pathResolver: ReadinessPathResolver | undefined;

export const registerReadinessPathResolver = (
  resolver: ReadinessPathResolver | undefined,
): void => {
  pathResolver = resolver;
};

/** Routing facts are fixture data; tenant enablement is applied afterward. */
function routingForAgent(
  agent: ReadinessAgent,
  { scenario, environment }: ReadinessContext,
): EnforcementReadiness {
  if (scenario === "none") return unrouted(environment, false);
  if (scenario === "ready") return routed(environment);

  if (agent.demoScenario) return routed(environment);
  if (agent.name.startsWith("Investment Research"))
    return unrouted(environment, true);
  if (agent.environmentType === "DEVELOPMENT")
    return unrouted(environment, hashString(agent.id) % 2 === 0);
  if (agent.environmentType === "TEST") return routed(environment);

  const slot =
    agent.sequenceIndex === undefined
      ? undefined
      : agent.sequenceIndex % SLOT_WINDOW;
  if (slot !== undefined && UNKNOWN_SLOTS.includes(slot)) {
    return {
      status: "UNKNOWN",
      method: environment.method,
      environmentConfigured: true,
      agentRoutedThroughEnforcement: false,
      routingObserved: false,
      healthStatus: "UNKNOWN",
      reason: "unverifiable",
    };
  }
  if (agent.businessUnit === "CUSTOMER_SUPPORT" && hashString(agent.id) % 6 === 0)
    return unrouted(environment, true);
  return routed(environment);
}

/**
 * The agent-level answer for a set of required paths: a confirmed blocker wins
 * over an unknown, an unknown wins over configured, and `NOT_APPLICABLE` paths
 * take no part. One configured connection never hides another path's gap.
 */
function foldPaths(
  base: EnforcementReadiness,
  connections: EnforcementPathFact[],
): EnforcementReadiness {
  const routing: EnforcementPathFact = {
    kind: "routing",
    state:
      base.status === "READY"
        ? "CONFIGURED"
        : base.status === "UNKNOWN"
          ? "UNKNOWN"
          : "SETUP_REQUIRED",
    reason: base.reason,
  };
  const paths = [routing, ...connections];
  // Only a routed, otherwise-ready agent is judged by its connections; every
  // other status already names a more fundamental gap and keeps it.
  if (base.status !== "READY") return { ...base, paths };
  const blocker = connections.find(path => path.state === "SETUP_REQUIRED");
  if (blocker)
    return {
      ...base,
      status: "SETUP_REQUIRED",
      reason: blocker.reason ?? "mcpConnectionMissing",
      paths,
    };
  if (connections.some(path => path.state === "UNKNOWN"))
    return { ...base, status: "UNKNOWN", reason: "unverifiable", paths };
  return { ...base, paths };
}

/** The sole dependency gate used by every mock endpoint and summary. */
export function deriveAgentReadiness(
  environment: EnforcementEnvironment,
  routing: EnforcementReadiness,
  connections: EnforcementPathFact[] = [],
): EnforcementReadiness {
  if (environment.status === "UNKNOWN") {
    return foldPaths(
      { ...routing, status: "UNKNOWN", reason: "unverifiable" },
      connections,
    );
  }
  // A disabled workspace does not erase what was configured: an agent whose
  // routing and connections are established stays configured, and the workspace
  // blocker is a separate fact every surface states alongside it. Only an agent
  // that was never routed reads as needing setup - and says why.
  if (!environment.enabled) {
    return foldPaths(
      { ...routing, environmentConfigured: environment.configured },
      connections,
    );
  }
  if (environment.status === "ATTENTION_REQUIRED" && routing.status === "READY") {
    return foldPaths(
      {
        ...routing,
        status: "ATTENTION_REQUIRED",
        healthStatus: "UNHEALTHY",
        reason: "gatewayUnhealthy",
      },
      connections,
    );
  }
  return foldPaths(routing, connections);
}

export function readinessForAgent(
  agent: ReadinessAgent,
  context: ReadinessContext = readinessContext(),
): EnforcementReadiness {
  return deriveAgentReadiness(
    context.environment,
    routingForAgent(agent, context),
    // The "ready" demo scenario is the one where everything is set up, so no
    // connection fixture may contradict it.
    context.scenario === "ready" ? [] : (pathResolver?.(agent, context.now) ?? []),
  );
}

export function matchesEnforcementFilter(
  status: EnforcementReadinessStatus,
  filter: string | undefined,
): boolean {
  if (!filter) return true;
  if (filter === "NOT_READY") return status !== "READY";
  // What the "need setup" count counts: known-incomplete setup, never unknown.
  if (filter === "SETUP_GAP")
    return status === "SETUP_REQUIRED" || status === "ATTENTION_REQUIRED";
  return status === filter;
}
